import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import { Modal, Grid, TextField, Button, Autocomplete, ListItem, ListItemText, Stack, Typography, Box } from '@mui/material';
import ModalLoading from '../../ModalLoading';
import MaskTelefone from '../../helpers/MaskTelefone';
import { cores } from '../../../styles/styleUtils';

import { useUnidades, isSameUnidade } from '../../../hooks/useUnidades';

import {
  getClientesPagina,
  updateCliente,
} from '../../api';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50vw',
  maxHeight: '95vh',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  backgroundColor: cores.cinzaClaro,
};

const formasCaptacao = ['Google', 'Tráfego Pago', 'E-mail', 'Lista de Prospecção'];
const sexos = ['Masculino', 'Feminino'];

const NewSaleForm = React.forwardRef((props, ref) => {
  const usuarioLogado = JSON.parse(localStorage.getItem('userData'));

  const { unidades } = useUnidades();

  // Estado do formulario principal
  // ATENÇÃO: usuarioLogado?.unidade pode ainda vir no esquema legado
  // ({ id, nome }, sem _id), caso o cadastro de usuário não tenha sido
  // migrado. Se isso acontecer, o Autocomplete de Unidade abaixo não vai
  // conseguir pré-selecionar esse valor (isOptionEqualToValue compara por
  // _id) e o campo aparecerá vazio mesmo com usuarioLogado.unidade preenchido.
  const [formData, setFormData] = useState({
    formaCaptacao: null,
    unidade: usuarioLogado?.unidade || null,
  });

  // Estado do cliente
  const [cliente, setCliente] = useState(null);

  const [modalLoading, setModalLoading] = useState(false);

  // Autocomplete
  const [clienteOptions, setClienteOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    fetchClientes('');
  }, []);

  const fetchClientes = async (value) => {
    try {
      const fetchedClientes = await getClientesPagina({ nome: value, page: 1, limit: 100 });
      if (fetchedClientes && fetchedClientes.clientes) {
        setClienteOptions(fetchedClientes.clientes);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleInputChangeClientAutocomplete = (event, value, reason) => {
    setInputValue(value);
    if (reason === 'input') {
      debouncedFetch(value);
    }
  };

  const debouncedFetch = useCallback(
    debounce((value) => {
      if (value.length >= 2 || value.length === 0) {
        fetchClientes(value);
      }
    }, 500),
    []
  );

  const handleSelectCliente = (event, value) => {
    if (value) {
      setCliente({
        ...value,
      });
    } else {
      setCliente(null);
    }
  };

  const handleAddNovoCliente = () => {
    setCliente(null);
    setInputValue('');
  };

  const handleChangeCliente = (e) => {

    const { name, value } = e.target;
    setCliente((prev) => ({ ...prev, [name]: value }));
  };

  const handleSalvarCliente = async () => {
    setModalLoading(true);
    try {
      const objToSave = { ...cliente, ignoreCpf: true };
      const resultado = await updateCliente(objToSave);
      if (resultado.ok) {
        setCliente(resultado.data?.newCliente || resultado.data);
        window.alert('Cliente salvo/atualizado com sucesso!');
      } else {
        window.alert(resultado.msg || 'Erro ao salvar cliente');
      }
    } catch (error) {
      console.error(error);
      window.alert('Erro ao salvar cliente');
    } finally {
      setModalLoading(false);
    }
  };

  const isClienteValido = cliente?.nome && cliente?.telefone && cliente?.sexo;

  const handleSubmit = async () => {
    if (!cliente._id) {
      window.alert('Por favor, salve ou selecione o cliente primeiro.');
      return;
    }
    if (!formData.formaCaptacao) {
      window.alert('Por favor, preencha a forma de captação.');
      return;
    }
    if (!formData.unidade) {
      window.alert('Por favor, selecione a unidade.');
      return;
    }

    const leadPayload = {
      acrescimo: 0,
      atividades: [],
      backlog: [],
      cliente,
      data: new Date(),
      dataagendamento: null,
      datainstalacao: null,
      desconto: 0,
      disjuntorcliente: {},
      etapa: {
        id: 0,
        nome: "Lead Cadastrado",
        color: "LightBlue"
      },
      faturas: [],
      finalizado: null,
      fluxo: {
        id: 10,
        nome: "Qualificação",
        etapas: [
          {
            id: 0,
            nome: "Lead Cadastrado",
            color: "LightBlue"
          },
          {
            id: 1,
            nome: "Tentativa de Contato",
            color: "LightGreen"
          },
          {
            id: 2,
            nome: "Qualificação do Lead",
            color: "Thistle"
          }
        ]
      },
      fornecedor: {},
      kit: null,
      localinstalacao: {},
      mediaconsumo: 0,
      mediaconsumoabatida: 0,
      modulo: null,
      negociacao: null,
      obs: "",
      orientacaotelhado: {},
      origem: {
        nome: "Lead"
      },
      produto: {
        _id: "69979a234002bbf92e75c0c6",
        nome: "Lead"
      },
      quantidadepaineis: null,
      responsavel: null,
      servicos: [],
      superficie: {},
      unidade: formData.unidade,
      user: usuarioLogado,
      valor: 0,
      valorkit: 0,
      valorprojeto: 0
    };

    setModalLoading(true);
    try {
      await props.onConfirm(leadPayload);
    } catch (error) {
      console.error(error);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <Modal open={props.open} onClose={props.handleClose}>
      <Box sx={style}>
        <Typography variant="h5" sx={{ mb: 3 }}>Novo Lead</Typography>

        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, mb: 3, backgroundColor: cores.branco }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Autocomplete
              disablePortal
              id="cliente-select"
              options={clienteOptions}
              value={cliente?._id ? cliente : null}
              onChange={handleSelectCliente}
              inputValue={inputValue}
              onInputChange={handleInputChangeClientAutocomplete}
              getOptionLabel={(option) => `${option.nome} - ${option.telefone} (${option._id})`}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              sx={{ flexGrow: 1 }}
              renderInput={(params) => <TextField {...params} label="Buscar Cliente Existente" variant="outlined" />}
              renderOption={(props, option) => (
                <ListItem {...props} key={option._id}>
                  <ListItemText primary={option.nome} secondary={option.telefone || option.cpf} />
                </ListItem>
              )}
            />
            <Button variant="outlined" onClick={handleAddNovoCliente}>
              + Novo Cliente
            </Button>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                name="nome"
                label="Nome"
                fullWidth
                value={cliente?.nome || ''}
                onChange={handleChangeCliente}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MaskTelefone
                name="telefone"
                id="telefone"
                label="Telefone"
                variant="outlined"
                fullWidth
                value={cliente?.telefone || ''}
                onChange={handleChangeCliente}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={sexos}
                value={cliente?.sexo || ''}
                onChange={(e, newValue) => handleChangeCliente({ target: { name: 'sexo', value: newValue } })}
                renderInput={(params) => <TextField {...params} label="Gênero" />}
              />
            </Grid>
          </Grid>

          {isClienteValido && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" color="primary" onClick={handleSalvarCliente}>
                Salvar / Atualizar Cliente
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, mb: 3, backgroundColor: cores.branco }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={formasCaptacao}
                value={formData.formaCaptacao}
                onChange={(e, newValue) => setFormData(prev => ({ ...prev, formaCaptacao: newValue }))}
                renderInput={(params) => <TextField {...params} label="Forma de Captação" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={unidades}
                getOptionLabel={(option) => option.nome || ''}
                isOptionEqualToValue={isSameUnidade}
                value={formData.unidade}
                onChange={(e, newValue) => setFormData(prev => ({ ...prev, unidade: newValue }))}
                renderInput={(params) => <TextField {...params} label="Unidade" />}
              />
            </Grid>
          </Grid>
        </Box>

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 'auto' }}>
          <Button variant="outlined" color="error" onClick={props.handleClose}>
            Cancelar
          </Button>
          <Button variant="contained" color="success" onClick={handleSubmit} disabled={!(cliente?.nome && cliente?.telefone && cliente?.sexo && formData.unidade && formData.formaCaptacao)}>
            Confirmar Lead
          </Button>
        </Stack>

        {modalLoading && <ModalLoading open={modalLoading} />}
      </Box>
    </Modal>
  );
});

export default NewSaleForm;